import { Configuration } from "Models/configuration";
import { LeadOffsets } from "Models/consumer";
import { Node } from "Models/node";
import { Partition, PartitionType } from "Models/partition";
import { updatePartition } from "Redux/actions";
import { getPartitions } from "Redux/selectors";
import store, { dispatch } from "Redux/store";
import { syncLogs } from "./logs.service";
import { getActiveNodes } from "./nodes.service";

export const getAllPartitions = (): Partition[] =>
    getPartitions(store.getState());

export const getActivePartitions = (partitions: Partition[]): Partition[] =>
    partitions.filter((p) => p.active);

export const getInactivePartitions = (partitions: Partition[]): Partition[] =>
    partitions.filter((p) => !p.active);

export const getLeads = (partitions: Partition[]): Partition[] =>
    partitions.filter((p) => p.type === PartitionType.LEAD);

export const getReplicas = (partitions: Partition[]): Partition[] =>
    partitions.filter((p) => p.type === PartitionType.REPLICA);

export const getActiveReplicas = (partitions: Partition[]): Partition[] =>
    getReplicas(partitions).filter((r) => r.active);

export const getActiveLeads = (partitions: Partition[]): Partition[] =>
    getLeads(partitions).filter((l) => l.active);

export const getActiveReplicasForLead = (
    lead: Partition,
    partitions: Partition[]
) => getActiveReplicas(partitions).filter((r) => r.id === lead.id);

const assignPartitionNodes = (
    nodes: Node[],
    partitions: Partition[],
    config: Configuration,
    replicas: number
) => {
    let leadsAssigned = new Set();

    //maybe shift first replica to end
    if (getActiveNodes(nodes).length === config.partitions) {
        const firstReplica = partitions
            .splice(partitions.length / replicas, 1)
            .shift();
        if (firstReplica) {
            partitions = partitions.filter((p) => p !== firstReplica);
            partitions.push(firstReplica);
        }
    }

    return partitions.reduce((acc: Partition[], p) => {
        if (!p.active) return [...acc, p];

        const nodeId = nodes
            .filter(
                (n) =>
                    n.active &&
                    !acc.some(
                        (part) => part.nodeId === n.id && part.id === p.id
                    )
            )
            .map((n) => n.id)
            .sort((a, b) => sortByLength(a, b, acc))
            .shift();

        if (!nodeId) return acc;

        const type = leadsAssigned.has(p.id)
            ? PartitionType.REPLICA
            : PartitionType.LEAD;
        leadsAssigned.add(p.id);

        return [
            ...acc,
            nodeId
                ? {
                      ...p,
                      nodeId: nodeId,
                      type,
                  }
                : p,
        ];
    }, []);
};

export const setupPartitions = (
    config: Configuration,
    currentPartitions: Partition[],
    nodes: Node[]
) => {
    const currentActivePartitions = getActivePartitions(currentPartitions);
    const currentInactivePartitions = getInactivePartitions(currentPartitions);

    const replicas =
        config.nodes < config.replicas ? config.nodes : config.replicas;

    let partitions = Array.from({ length: replicas }, (_, ridx) =>
        Array.from(
            { length: config.partitions },
            (_, pidx) =>
                currentActivePartitions.filter((p) => p.id === pidx + 1)[
                    ridx
                ] ?? new Partition(pidx + 1)
        )
    ).flat();

    partitions = assignPartitionNodes(nodes, partitions, config, replicas);
    return [...partitions, ...currentInactivePartitions];
};

export const updateOffsets = (offsets: LeadOffsets, group: string) => {
    const partitions = getActivePartitions(getAllPartitions());
    const updateIds = Object.keys(offsets).map((k) => +k);

    const newPartitions = partitions
        .filter((p) => updateIds.includes(p.id))
        .map((p) => ({
            ...p,
            offset: {
                ...p.offset,
                [group]: offsets[p.id],
            },
        }));
    newPartitions?.forEach((p) => dispatch(updatePartition(p)));
};

export const syncReplicas = (partitions: Partition[]) => {
    const leads = getActiveLeads(partitions);

    leads.map((l) => {
        return getActiveReplicasForLead(l, partitions).forEach((r) => {
            syncLogs(r.logUid, l.logUid);
        });
    });
};

const nodePartitionsLength = (nodeId: number, partitions: Partition[]) => {
    return partitions.reduce(
        (acc, p) => (p.nodeId === nodeId ? acc + 1 : acc),
        0
    );
};

const sortByLength = (aId: number, bId: number, partitions: Partition[]) =>
    nodePartitionsLength(aId, partitions) -
    nodePartitionsLength(bId, partitions);
